import { create } from 'zustand';
import type { SeoHealthDto, SeoIssue, SeoIssueItemDto } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';

/** SEO health (ADR-095): the counts, and the items behind the issue opened — paged on the server. */
interface SeoHealthState {
  health: SeoHealthDto | null;
  loading: boolean;
  error: string | null;
  issue: SeoIssue | null;
  items: SeoIssueItemDto[];
  nextCursor: string | null;
  itemsLoading: boolean;

  load: () => Promise<void>;
  open: (issue: SeoIssue) => Promise<void>;
  more: () => Promise<void>;
}

const PAGE_SIZE = 20;
let itemsRequest = 0;

export const useSeoHealthStore = create<SeoHealthState>((set, get) => ({
  health: null,
  loading: false,
  error: null,
  issue: null,
  items: [],
  nextCursor: null,
  itemsLoading: false,

  async load() {
    set({ loading: true, error: null });
    try {
      const health = await withSession(() => adminApi.seo.health());
      set({ health, loading: false });
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not check the store’s SEO.') });
    }
  },

  async open(issue) {
    const request = ++itemsRequest;
    set({ issue, items: [], nextCursor: null, itemsLoading: true });
    try {
      const page = await withSession(() => adminApi.seo.issues({ issue, limit: PAGE_SIZE }));
      if (request !== itemsRequest) return;
      set({ items: page.items, nextCursor: page.meta.nextCursor, itemsLoading: false });
    } catch (error) {
      if (request !== itemsRequest) return;
      set({ itemsLoading: false, error: errorMessage(error, 'Could not list them.') });
    }
  },

  async more() {
    const { issue, nextCursor, itemsLoading } = get();
    if (!issue || !nextCursor || itemsLoading) return;
    const request = itemsRequest;
    set({ itemsLoading: true });
    try {
      const page = await withSession(() => adminApi.seo.issues({ issue, limit: PAGE_SIZE, cursor: nextCursor }));
      if (request !== itemsRequest) return;
      set((state) => ({ items: [...state.items, ...page.items], nextCursor: page.meta.nextCursor, itemsLoading: false }));
    } catch (error) {
      set({ itemsLoading: false, error: errorMessage(error, 'Could not list more.') });
    }
  },
}));
